import React, { useState, useEffect, useRef } from "react";
import { SURFACE } from "./constants/colors";
import { kanbanService } from "../../utils/services/kanbanService";
import MetricStrip from "./components/MetricStrip";
import FilterBar from "./components/FilterBar";
import BoardView from "./components/BoardView";
import PipelineView from "./components/PipelineView";
import MetricsView from "./components/MetricsView";
import StatusBar from "./components/StatusBar";
import wsManager from "../../utils/websocketManager";
import { useKanbanWebSocketEvents } from "./hooks/useKanbanWebSocketEvents";

// CSS for card animations
const ANIMATION_STYLES = `
  @keyframes dragFlip {
    0% {
      opacity: 0;
      transform: translateX(-40px) translateY(20px) rotateZ(-5deg) scale(0.8);
      box-shadow: 0 0 0 rgba(76, 175, 80, 0);
    }
    60% {
      opacity: 1;
      transform: translateX(0) translateY(0) rotateZ(0deg) scale(1.05);
      box-shadow: 0 12px 24px rgba(76, 175, 80, 0.4);
    }
    100% {
      opacity: 1;
      transform: translateX(0) translateY(0) rotateZ(0deg) scale(1);
      box-shadow: 0 8px 20px rgba(76, 175, 80, 0.25);
    }
  }

  @keyframes dragOut {
    0% {
      opacity: 1;
      transform: translateX(0) translateY(0) rotateZ(0deg) scale(1);
      box-shadow: 0 8px 20px rgba(76, 175, 80, 0.25);
    }
    50% {
      opacity: 0.7;
      transform: translateX(15px) translateY(-25px) rotateZ(3deg) scale(1.1);
      box-shadow: 0 15px 30px rgba(76, 175, 80, 0.3), inset 0 0 15px rgba(255, 255, 255, 0.2);
    }
    100% {
      opacity: 0;
      transform: translateX(40px) translateY(0) rotateZ(5deg) scale(0.8);
      box-shadow: 0 0 20px rgba(76, 175, 80, 0.1);
    }
  }

  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 8px 20px rgba(76, 175, 80, 0.25);
    }
    50% {
      box-shadow: 0 8px 30px rgba(76, 175, 80, 0.5), inset 0 0 10px rgba(76, 175, 80, 0.1);
    }
  }

  @keyframes slideInWithWave {
    0% {
      opacity: 0;
      transform: translateX(-50px) scale(0.9);
    }
    70% {
      opacity: 1;
      transform: translateX(5px) scale(1.02);
    }
    100% {
      opacity: 1;
      transform: translateX(0) scale(1);
    }
  }

  .kanban-card-new {
    animation: dragFlip 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
    box-shadow: 0 8px 20px rgba(76, 175, 80, 0.25) !important;
    position: relative;
  }

  .kanban-card-new::before {
    content: '';
    position: absolute;
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
    background: radial-gradient(circle, rgba(76, 175, 80, 0.3) 0%, transparent 70%);
    border-radius: inherit;
    pointer-events: none;
    animation: pulse 1s ease-in-out infinite;
  }

  .kanban-card-removing {
    animation: dragOut 0.6s cubic-bezier(0.4, 0, 0.6, 1) forwards !important;
  }

  .kanban-card-moving {
    animation: slideInWithWave 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
    box-shadow: 0 12px 28px rgba(76, 175, 80, 0.35) !important;
  }
`;

// Inject CSS
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.innerHTML = ANIMATION_STYLES;
  document.head.appendChild(style);
}

const SupplyChainKanban = () => {
  // ── Data state (served by service) ──────────────────────────────
  const [cards, setCards] = useState([]);
  const [metrics, setMetrics] = useState({
    warehouse_value: {},
    shipment_value: {},
    fulfillment_rate: {},
  });

  // ── UI state ────────────────────────────────────────────────────
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [activeView, setActiveView] = useState("board");
  const client_id = localStorage.getItem("client_id");
  const user = localStorage.getItem("user");
  const parsedUser = user ? JSON.parse(user) : {};
  const isAdmin = parsedUser?.role === "admin";
  const [dateRange, setDateRange] = useState({ preset: "7d" });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const triggerRefresh = () => setRefreshTrigger((t) => t + 1);
  const [isFetching, setIsFetching] = useState(false);

  // ── Pending moves: track optimistic card moves so stale cache
  //    data cannot flash the card back to its old stage ───────────
  // Map of { cardId: { fromKey, toKey, movedCard } }
  const pendingMovesRef = useRef({});

  const recordPendingMove = (cardId, fromKey, toKey, movedCard) => {
    pendingMovesRef.current[cardId] = { fromKey, toKey, movedCard };
    // Auto-clear after 8s (well after any WS push or re-fetch resolves)
    setTimeout(() => {
      delete pendingMovesRef.current[cardId];
    }, 8000);
  };

  // Admin-only client filter
  const [selectedClient, setSelectedClient] = useState(null);
  // Additional filters
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedWms, setSelectedWms] = useState(null);

  // The effective client_id: admin may select a different client
  const effectiveClientId = selectedClient?._id || client_id;

  // Compute actual start/end dates from preset
  const resolveDateRange = (dr) => {
    const now = new Date();
    if (!dr || dr.preset === "30d") {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      return { startDate: start, endDate: now };
    }
    if (dr.preset === "7d") {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      return { startDate: start, endDate: now };
    }
    if (dr.preset === "15d") {
      const start = new Date(now);
      start.setDate(start.getDate() - 15);
      return { startDate: start, endDate: now };
    }
    if (dr.preset === "this_week") {
      const start = new Date(now);
      const day = start.getDay(); // 0=Sun
      const diff = day === 0 ? -6 : 1 - day; // Monday
      start.setDate(start.getDate() + diff);
      start.setHours(0, 0, 0, 0);
      return { startDate: start, endDate: now };
    }
    if (dr.preset === "custom" && dr.startDate && dr.endDate) {
      return {
        startDate: new Date(dr.startDate),
        endDate: new Date(dr.endDate),
      };
    }
    // fallback: last 30 days
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return { startDate: start, endDate: now };
  };

  // ── WebSocket events management via custom hook ─────────────────
  const { newCardIds, removingCardIds, movingCardIds } =
    useKanbanWebSocketEvents(cards, setCards, setMetrics);

  // ── Helper: compute metric-strip values from a full cards object ──
  const computeMetrics = (data) => {
    const sumValue = (arr) =>
      (arr || []).reduce((sum, card) => sum + Number(card.total_value || 0), 0);
    const sumCount = (arr) =>
      (arr || []).reduce((sum, card) => sum + Number(card.qty || 0), 0);
    return {
      warehouse_value: {
        value: sumValue(data.inStock) + sumValue(data.fulfillment),
        count: sumCount(data.inStock) + sumCount(data.fulfillment),
      },
      in_transit_supplier: {
        value: sumValue(data.inTransit),
        count: sumCount(data.inTransit),
      },
      in_transit_delivery: {
        value: sumValue(data.pendingDelivery),
        count: sumCount(data.pendingDelivery),
      },
      pipeline_value: {
        value: Object.values(data).reduce(
          (a, b) =>
            a +
            (b || []).reduce(
              (sum, card) => sum + Number(card.total_value || 0),
              0,
            ),
          0,
        ),
        count: Object.values(data).reduce(
          (a, b) =>
            a + (b || []).reduce((sum, card) => sum + Number(card.qty || 0), 0),
          0,
        ),
      },
    };
  };

  // ── Helper: apply fetched kanban data to state ──────────────────
  const applyKanbanData = (fetchedKanbanData) => {
    // Re-apply any in-flight optimistic moves so a stale cache hit
    // cannot flash a card back to its old stage.
    const pending = pendingMovesRef.current;
    let data = fetchedKanbanData;
    if (Object.keys(pending).length > 0) {
      data = { ...fetchedKanbanData };
      Object.entries(pending).forEach(
        ([cardId, { fromKey, toKey, movedCard }]) => {
          // If the fetched data still has the card in the old stage, move it
          const inFrom = (data[fromKey] || []).some((c) => c._id === cardId);
          const inTo = (data[toKey] || []).some((c) => c._id === cardId);
          if (inFrom && !inTo) {
            data[fromKey] = (data[fromKey] || []).filter(
              (c) => c._id !== cardId,
            );
            data[toKey] = [...(data[toKey] || []), movedCard];
          }
        },
      );
    }
    setCards(data);
    setMetrics(computeMetrics(data));
  };

  // ── Clear stale cards immediately when the user changes filters ──
  // (not on refreshTrigger – that's a post-move refresh where the
  //  optimistic state is already correct and should not flicker)
  const filterChangeRef = useRef(false);
  useEffect(() => {
    if (!filterChangeRef.current) {
      filterChangeRef.current = true;
      return; // skip initial mount
    }
    setCards({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveClientId,
    dateRange,
    selectedChannel,
    selectedSupplier,
    selectedWms,
  ]);

  // ── WebSocket connection (initialize once) ──────────────────────
  useEffect(() => {
    wsManager.connect(JSON.parse(user)._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── WebSocket: apply background-refreshed kanban data ───────────
  // Only apply if the pushed filter fingerprint matches current active filters.
  useEffect(() => {
    const fmtDate = (d) => {
      if (!d) return "all";
      const dt = d instanceof Date ? d : new Date(d);
      return isNaN(dt) ? "all" : dt.toISOString().slice(0, 10);
    };

    const unsub = wsManager.on("kanbanDataRefresh", (msg) => {
      // Rebuild the same fingerprint used by the backend cache key
      const { startDate, endDate } = resolveDateRange(dateRange);
      const current = {
        client_id: effectiveClientId || "all",
        category: "all",
        supplier_id: selectedSupplier?._id || "all",
        channel: selectedChannel?.name || "all",
        wms_id: selectedWms?._id || "all",
        sd: fmtDate(startDate),
        ed: fmtDate(endDate),
      };
      const pushed = {
        client_id: msg.filters?.client_id || "all",
        category: msg.filters?.category || "all",
        supplier_id: msg.filters?.supplier_id || "all",
        channel: msg.filters?.channel || "all",
        wms_id: msg.filters?.wms_id || "all",
        sd: fmtDate(msg.filters?.startDate),
        ed: fmtDate(msg.filters?.endDate),
      };
      const matches = Object.keys(current).every(
        (k) => current[k] === pushed[k],
      );
      if (matches && msg.data) {
        console.log("📡 [WS] Applying background refresh for current filters");
        applyKanbanData(msg.data);
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveClientId,
    dateRange,
    selectedChannel,
    selectedSupplier,
    selectedWms,
  ]);

  // ── WebSocket: append streamed batches while a cache-MISS fetch is
  //    still paginating through the remaining data on the server ────
  useEffect(() => {
    const fmtDate = (d) => {
      if (!d) return "all";
      const dt = d instanceof Date ? d : new Date(d);
      return isNaN(dt) ? "all" : dt.toISOString().slice(0, 10);
    };

    const unsub = wsManager.on("kanbanDataAppend", (msg) => {
      const { startDate, endDate } = resolveDateRange(dateRange);
      const current = {
        client_id: effectiveClientId || "all",
        category: "all",
        supplier_id: selectedSupplier?._id || "all",
        channel: selectedChannel?.name || "all",
        wms_id: selectedWms?._id || "all",
        sd: fmtDate(startDate),
        ed: fmtDate(endDate),
      };
      const pushed = {
        client_id: msg.filters?.client_id || "all",
        category: msg.filters?.category || "all",
        supplier_id: msg.filters?.supplier_id || "all",
        channel: msg.filters?.channel || "all",
        wms_id: msg.filters?.wms_id || "all",
        sd: fmtDate(msg.filters?.startDate),
        ed: fmtDate(msg.filters?.endDate),
      };
      const matches = Object.keys(current).every(
        (k) => current[k] === pushed[k],
      );
      if (!matches || !msg.data) return;

      console.log("📡 [WS] Appending streamed kanban batch", msg.data);
      setCards((prev) => {
        const merged = { ...prev };
        Object.entries(msg.data).forEach(([stage, items]) => {
          if (!items || !items.length) return;
          const existingIds = new Set((merged[stage] || []).map((c) => c._id));
          const newItems = items.filter((c) => !existingIds.has(c._id));
          merged[stage] = [...(merged[stage] || []), ...newItems];
        });
        setMetrics(computeMetrics(merged));
        return merged;
      });
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveClientId,
    dateRange,
    selectedChannel,
    selectedSupplier,
    selectedWms,
  ]);

  // ── Fetch data on mount / category change / date range change ───
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setIsFetching(true);
    const load = async () => {
      try {
        const { startDate, endDate } = resolveDateRange(dateRange);
        const fetchedKanbanData = await kanbanService.fetchData(
          {
            client_id: effectiveClientId,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            ...(selectedChannel && { channel: selectedChannel.name }),
            ...(selectedSupplier && { supplier_id: selectedSupplier._id }),
            ...(selectedWms && { wms_id: selectedWms._id }),
          },
          signal,
        );
        applyKanbanData(fetchedKanbanData);
      } catch (err) {
        if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
          console.error("Failed to load kanban data", err);
        }
      } finally {
        if (!signal.aborted) setIsFetching(false);
      }
    };
    load();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveClientId,
    dateRange,
    selectedChannel,
    selectedSupplier,
    selectedWms,
    refreshTrigger,
  ]);

  return (
    <div
      style={{
        height: "calc(100vh - 100px)",
        background: SURFACE.pageBg,
        fontFamily: "Poppins",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* <TopNav
        activeView={activeView}
        setActiveView={setActiveView}
        blocked={blocked}
        atRisk={atRisk}
      /> */}

      <MetricStrip cards={[]} metrics={metrics} />

      <FilterBar
        isAdmin={isAdmin}
        selectedClient={selectedClient}
        setSelectedClient={setSelectedClient}
        selectedChannel={selectedChannel}
        setSelectedChannel={setSelectedChannel}
        selectedSupplier={selectedSupplier}
        setSelectedSupplier={setSelectedSupplier}
        selectedWms={selectedWms}
        setSelectedWms={setSelectedWms}
        dateRange={dateRange}
        setDateRange={setDateRange}
      />

      {activeView === "board" && (
        <BoardView
          cards={cards}
          dragOver={dragOver}
          setDragOver={setDragOver}
          dragId={dragId}
          setDragId={setDragId}
          setCards={setCards}
          newCardIds={newCardIds}
          removingCardIds={removingCardIds}
          movingCardIds={movingCardIds}
          client_id={client_id}
          onRefresh={triggerRefresh}
          isFetching={isFetching}
          onCardMoved={recordPendingMove}
        />
      )}

      {activeView === "pipeline" && <PipelineView cards={cards} />}

      {activeView === "metrics" && <MetricsView cards={cards} />}

      <StatusBar />
    </div>
  );
};

export default SupplyChainKanban;
